import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AuthResponse,
  checkUserExistService,
  emailLogin,
  loadProfile,
  refreshAuthAccessToken,
  deleteCookie,
  setCookie,
  IUser,
  getCookie,
} from '../services';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/constants';

/**
 * No `userExists` here on purpose. The API no longer reports whether an address
 * is registered, so an unauthenticated caller cannot enumerate accounts. New
 * and returning users see the same screen.
 */
interface RequestOtpResult {
  success: boolean;
}

interface AuthContextState {
  user: Partial<IUser> | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;

  requestEmailOtp(email: string): Promise<RequestOtpResult>;
  verifyEmailOtp(
    email: string,
    otp: string
  ): Promise<{
    success: boolean;
    data: AuthResponse;
  }>;
  refreshUser(): Promise<void>;
  refreshAccessToken(opts?: { silent?: boolean }): Promise<string | null>;
  logout(): void;
}

const AuthContext = createContext<AuthContextState | undefined>(undefined);

export const useAuth = (): AuthContextState => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<Partial<IUser> | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = Boolean(accessToken);

  const persistAccessToken = (token: string | null) => {
    setAccessToken(token);
    token
      ? setCookie(ACCESS_TOKEN_KEY, token, 7)
      : deleteCookie(ACCESS_TOKEN_KEY);
  };

  const persistUser = (userData: IUser | null) => {
    if (userData) {
      setCookie('tenantId', userData.tenantId, 7);
      setCookie(
        'user',
        JSON.stringify({
          id: userData.id,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
        }),
        7
      );
      setUser({
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        tenantId: userData.tenantId,
      });
    } else {
      deleteCookie('tenantId');
      deleteCookie('user');
      setUser(null);
    }
  };

  const persistRefreshToken = (token: string | null) => {
    setRefreshToken(token);
    token
      ? setCookie(REFRESH_TOKEN_KEY, token, 7)
      : deleteCookie(REFRESH_TOKEN_KEY);
  };

  const resetAuth = useCallback(() => {
    setUser(null);
    persistAccessToken(null);
    persistRefreshToken(null);
    persistUser(null);
  }, []);

  /**
   * Re-issue the access token before it expires.
   *
   * Authenticated by the CURRENT access token, not a refresh token: login has
   * never issued one, so the previous implementation bailed on the first line
   * and logged the user out instead of refreshing. It also picks up business
   * roles granted since the token was signed.
   *
   * `silent: true` opts out of the logout-on-failure behavior below: it's for
   * best-effort refreshes (e.g. right after account setup, to pick up new
   * claims before landing on the dashboard) where the caller doesn't want a
   * transient failure to tear down a session that a moment ago was valid
   * enough to authenticate the very request that triggered the refresh.
   * Default (non-silent) callers — the pre-expiry refresh in services/api.ts
   * — legitimately mean "this session can no longer authenticate" on failure.
   */
  const refreshAccessToken = useCallback(
    async (opts?: { silent?: boolean }): Promise<string | null> => {
      if (!accessToken) {
        if (!opts?.silent) resetAuth();
        return null;
      }

      try {
        const response = await refreshAuthAccessToken(accessToken);
        if (!response?.token) {
          if (!opts?.silent) resetAuth();
          return null;
        }

        persistAccessToken(response.token);
        return response.token;
      } catch {
        if (!opts?.silent) resetAuth();
        return null;
      }
    },
    [accessToken, resetAuth]
  );

  const refreshUser = useCallback(async () => {
    if (!accessToken) return;

    const profile = await loadProfile(accessToken, refreshAccessToken);
    setUser(profile?.data || null);
  }, [accessToken, refreshAccessToken]);

  useEffect(() => {
    const bootstrap = async () => {
      const storedAccessToken = getCookie(ACCESS_TOKEN_KEY);
      const storedRefreshToken = getCookie(REFRESH_TOKEN_KEY);

      if (!storedAccessToken) {
        setLoading(false);
        return;
      }

      persistAccessToken(storedAccessToken);
      persistRefreshToken(storedRefreshToken);

      await refreshUser();
      setLoading(false);
    };

    bootstrap();
  }, [refreshUser]);

  const requestEmailOtp = useCallback(
    async (email: string): Promise<RequestOtpResult> => {
      if (!email) {
        throw new Error('Email is required');
      }
      const response = await checkUserExistService(email);
      return {
        success: response?.success ?? false,
      };
    },
    []
  );

  const verifyEmailOtp = useCallback(
    async (
      email: string,
      otp: string
    ): Promise<{
      success: boolean;
      data: AuthResponse;
    }> => {
      if (!email || !otp) {
        throw new Error('Email and OTP are required');
      }
      const response: { success: boolean; data: AuthResponse } | null =
        await emailLogin(email, otp);
      if (!response?.success) {
        throw new Error('Invalid OTP');
      }
      setUser(response.data.user);
      persistAccessToken(response.data.token);
      persistRefreshToken(response.data.refreshToken);
      persistUser(response.data.user);
      return response;
    },
    []
  );

  const logout = useCallback(() => {
    resetAuth();
  }, [resetAuth]);

  const value = useMemo<AuthContextState>(
    () => ({
      user,
      accessToken,
      refreshToken,
      isAuthenticated,
      loading,
      requestEmailOtp,
      verifyEmailOtp,
      refreshUser,
      refreshAccessToken,
      logout,
    }),
    [
      user,
      accessToken,
      refreshToken,
      isAuthenticated,
      loading,
      requestEmailOtp,
      verifyEmailOtp,
      refreshUser,
      refreshAccessToken,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
